import React, { forwardRef, useEffect, useMemo, useState, useRef } from 'react';
// Components
// eslint-disable-next-line no-unused-vars
import { MultiSelect, MultiSelectProps } from '@progress/kendo-react-dropdowns';
// Helpers
import { filterBy } from '@progress/kendo-data-query';
import cn from 'classnames';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
// Consts
import { ENTER_KEY_CODE, TAB_KEY_CODE } from 'shared/../constants/keyCodes';

const SELECT_ALL_KEY = 'SELECT_ALL';

const ReadOnlyMultiSelect = ({
  value, data, textField, dataItemKey, ...props
}) => {
  const className = cn('read-only', { empty: value?.length === 0 });

  if (!value || value.length === 0) {
    return (
      <div className={className} {...props}>
        No items selected
      </div>
    );
  }

  if (value && data.length === value.length) {
    return (
      <div className={className} {...props}>
        All
      </div>
    );
  }

  return (
    <div className='flex' {...props}>
      {value.map(
        (item, index) => (
          <span className='read-only' style={{ flexDirection: 'row' }} key={item[dataItemKey]}>
            {index > 0 && <span className='text-secondary'>,&nbsp;</span>}
            {item[textField]}
          </span>
        ),
      )}
    </div>
  );
};

const tagRender = (tagData, li) => React.cloneElement(li, li.props, [
  <span key={tagData?.text} style={tagData?.data?.[0]?._notFound ? { backgroundColor: 'gray' } : undefined}>
    {tagData?.text}
  </span>,
  li.props.children,
]);

const defaultData = [];
const emptyObject = {};

/**
 * @typedef {React.PropsWithoutRef<MultiSelectProps>} TelerikMultiSelectProps
 *
 * @typedef {Object} AdditionalMultiSelectProps
 * @property {string} [dataItemKey='value'] "value" by default.
 * @property {string} [textField='description'] "description" by default.
 * @property {string} [dataTestId] Data test Id.
 * @property {boolean} [primitive={false}] Indicates if the data is a flat array and a value is a key. False by default.
 * @property {boolean} [keysOnly={false}] Indicates if only keys are used as a value. False by default.
 * @property {boolean} [hideSelected={false}] Indicates if selected values are hidden from the dropdown options. False by default.
 * @property {boolean} [hasSelectAll={false}] Indicates if "Select all" feature is enabled to select all the dropdown options. False by default.
 * @property {boolean} [autoFilterById={true}] Auto-filter by ID. True by default.
 * @property {React.HTMLElement} [Wrapper='span'] Wrapper element. "span" by default.
 * @property {React.CSSProperties} [wrapperStyle] Wrapper style.
 * @property {string} [wrapperClassname] Wrapper class name.
 *
 * @param {TelerikMultiSelectProps & AdditionalMultiSelectProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const BaseMultiSelect = ({
  // Data
  data = defaultData,
  primitive,
  dataItemKey = primitive ? undefined : 'value',
  textField = primitive ? undefined : 'description',
  value,
  keysOnly = false,
  // UX
  hideSelected = false,
  hasSelectAll = false,
  readOnly = false,
  autoClose = false,
  // Filter
  filterable = true,
  autoFilterById = true,
  // Handlers
  onChange,
  onFocus,
  onBlur,
  dataTestId,
  Wrapper = 'span',
  wrapperStyle = emptyObject,
  wrapperClassname = '',
  ...others
}, ref) => {
  const multiSelectRef = useRef(null);
  const getKeys = (items) => items?.map((item) => item[dataItemKey] ?? item.value ?? item.id ?? item);

  const getValueByKeys = (keys) => {
    if (keys == null) return [];

    const normalizedKeys = Array.isArray(keys) ? keys : [keys];

    return normalizedKeys.map((key) =>
      data.find((i) => i[dataItemKey] == key) ??
      { [dataItemKey]: key, [textField]: `${key} - Not Found`, _notFound: true }
    );
  };

  // Backend may choose to return less data on the DTO of the value that is on the data options api DTO.
  // Merging these ensures type consistancy.
  const mergeValuesWithOptionValues = (values) => values?.map((v) => {
    const option = data.find((d) => d[dataItemKey] == (v[dataItemKey] ?? v.value ?? v.id ?? v));
    return option ? { ...option, ...v } : v;
  });

  const innerValue = keysOnly ? getValueByKeys(value) : mergeValuesWithOptionValues(value);

  // Support open on focus and close on blur
  const [opened, setOpened] = useState(false);

  if (readOnly) {
    return (
      <ReadOnlyMultiSelect
        data={data}
        value={innerValue || []}
        dataItemKey={dataItemKey}
        textField={textField}
        data-testid={dataTestId}
      />
    );
  }

  // Data
  const selectAllOption = { [dataItemKey]: SELECT_ALL_KEY, [textField]: 'Select all' };

  // Options (data) state
  const selected = useMemo(() => (keysOnly ? value : getKeys(value)), [value, keysOnly]);

  const [options, setOptions] = useState(data || []);
  const [filter, setFilter] = useState();

  const handleAutoFilterById = (opts) => {
    const dataWithStrId = opts?.map((d) => ({
      ...d,
      _dataItemKeyStr: d[dataItemKey]?.toString(),
    }));

    const labelFiltered = filterBy(dataWithStrId, filter);

    const idFilter = {
      field: '_dataItemKeyStr',
      operator: 'contains',
      ignoreCase: true,
      value: filter?.value,
    };

    const idFiltered = filterBy(dataWithStrId, idFilter);

    const cleanDt = [...labelFiltered, ...idFiltered]?.map((row) => {
      const { _dataItemKeyStr, ...rest } = row;
      return rest;
    });
    const newDt = uniq(cleanDt, dataItemKey);
    setOptions(newDt);
  };

  useEffect(() => {
    const notEmpty = data ? data.filter((item) => (primitive ? item : (!isNil(item[textField])))) : [];
    const visibleData = hideSelected ? notEmpty.filter((item) => !selected.includes(item[dataItemKey])) : notEmpty;
    const newOptions = hasSelectAll && visibleData.length > 0 ? [selectAllOption, ...visibleData] : visibleData;

    if (autoFilterById && filter) {
      handleAutoFilterById(newOptions);
    } else if (filterable && filter) {
      setOptions(filterBy(newOptions, filter));
    } else {
      setOptions(newOptions);
    }
  }, [
    hideSelected,
    data,
    selected,
    filterable,
    filter,
    autoFilterById,
  ]);

  const onFilterChange = ({ filter, nativeEvent }) => {
    // Do not reset filter if an item is selected. Telerik tries to reset it automatically.
    if (!autoClose && (nativeEvent?.type === 'click' || nativeEvent?.keyCode === ENTER_KEY_CODE)) {
      return;
    }
    setFilter(filter);
  };

  const handleFocus = (e) => {
    if (!others.disabled && !readOnly) {
      setOpened(true);
    }
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e) => {
    setOpened(false);
    if (filterable) {
      onFilterChange({ filter: null });
    }
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleChange = (e) => {
    if (filterable && autoClose) {
      onFilterChange({ filter: null });
    } else if (filterable) {
      // Select the filter text after an item is selected
      setTimeout(() => {
        const inputElement = multiSelectRef.current?.element?.querySelector('.k-input-inner');
        if (inputElement && inputElement.value) {
          inputElement.select();
        }
      }, 0);
    }
    const { value: newSelected } = e;
    const keys = getKeys(newSelected);

    if (!hasSelectAll) {
      onChange({ ...e, value: keysOnly ? keys : newSelected });
      return;
    }
    const isSelectedAll = newSelected.find((item) => item[dataItemKey] === SELECT_ALL_KEY);
    if (isSelectedAll) {
      onChange({ ...e, value: keysOnly ? getKeys(data) : data });
    } else {
      onChange({ ...e, value: keysOnly ? keys : newSelected });
    }
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === ENTER_KEY_CODE && e.target.ariaExpanded === 'true') {
      // Enter key was pressed
      e.preventDefault(); // Prevents dropdown from closing after pressing the enter key
      e.stopPropagation();
      // Get the element focused and its value
      const focused = document.getElementsByClassName('k-list-item k-focus')[0];
      const text = focused.childNodes[0].innerText;
      const selectedValue = options.find((o) => o[textField] === text)[dataItemKey];

      // Logic for select/unselect
      let newSelected = selected;
      if (newSelected.includes(selectedValue)) {
        newSelected = newSelected.filter((s) => s !== selectedValue);
      } else {
        newSelected.push(selectedValue);
      }

      // Set the array with the whole object, not just the value
      newSelected = newSelected.map((value) => options.find((o) => o[dataItemKey] === value));
      handleChange({ target: { value: newSelected } });
    } else if (e.keyCode === TAB_KEY_CODE) {
      onFilterChange({ filter: null });
    }
  };

  return (
    <Wrapper style={wrapperStyle} className={wrapperClassname} onKeyDown={handleKeyDown} data-testid={dataTestId}>
      <MultiSelect
        onChange={handleChange}
        data={options}
        ref={(r) => {
          // Maintain the forwarded ref while also keeping a local ref
          if (typeof ref === 'function') {
            ref(r);
          } else if (ref) {
            ref.current = r;
          }
          multiSelectRef.current = r;
        }}
        value={innerValue || defaultData}
        dataItemKey={dataItemKey}
        textField={textField}
        tagRender={tagRender}
        filterable={filterable}
        onFilterChange={onFilterChange}
        filter={filter?.value ?? null}
        autoClose={autoClose}
        opened={opened}
        onBlur={handleBlur}
        onFocus={handleFocus}
        {...others}
      />
    </Wrapper>
  );
};

/**
 * A base multi select component with various options and handlers.
 *
 * @param {TelerikMultiSelectProps & AdditionalMultiSelectProps} props Component props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Base multi select component.
 */
const ForwardMultiSelect = forwardRef(BaseMultiSelect);

export default ForwardMultiSelect;
